import { useState, useEffect, useCallback, startTransition, FC } from 'react';

import { SettingsContentWrapper } from '@legacy/components/layout/SettingsContentWrapper';
import { Button, Stack, Divider, Alert, Snackbar } from '@mui/material';
import { GridEventListener } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';

import { AccountingPeriodDataGrid } from './AccountingPeriodDataGrid';
import { AccountingPeriodDialog } from './AccountingPeriodDialog';
import AccountingPeriodModeSelector from './AccountingPeriodModeSelector';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { useRowHighlighting } from './hooks/useRowHighlighting';
import { YearFilter } from './YearFilter';
import {
  useGetAccountingPeriods,
  useCreateAccountingPeriod,
  useUpdateAccountingPeriod,
  useUpdateAccountingPeriodStatus,
  useGetAccountingPeriodById,
  useGetAccountingPeriodConfiguration,
  useUpdateAccountingPeriodConfiguration,
  useGetAccountingPeriodMetadata,
  useUpdateAccountingPeriodMode,
} from '@/api/REST/accountingPeriods';
import {
  AccountingPeriod,
  AccountingPeriodStatus,
  CreateAccountingPeriodRequest,
  UpdateAccountingPeriodRequest,
  UpdateAccountingPeriodStatusRequest,
  AccountingPeriodConfiguration,
} from '@/api/REST/accountingPeriods';
import { ConfirmationDialog } from '@/components/global/ConfirmationDialog';

// API integration - no more mock data

interface AccountingPeriodPageProps {
  title?: string;
  description?: string;
  initialYear?: number;
  onPeriodChange?: (periods: AccountingPeriod[]) => void;
}

const AccountingPeriodPage: FC<AccountingPeriodPageProps> = ({
  title,
  description,
  initialYear = new Date().getFullYear(),
}) => {
  const { t } = useTranslation('systemAdministrationNavigation');

  // Utility function to ensure dates are in YYYY-MM-DD format
  const ensureYYYYMMDD = (value: string): string => {
    const trimmed = value.trim();

    // Check if already in YYYY-MM-DD format
    const yyyyMMddRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (yyyyMMddRegex.test(trimmed)) {
      return trimmed;
    }

    // Handle ISO date strings (YYYY-MM-DDTHH:mm:ss)
    const isoDateRegex = /^(\d{4}-\d{2}-\d{2})T/;
    const isoMatch = trimmed.match(isoDateRegex);
    if (isoMatch) {
      return isoMatch[1]; // Return just the YYYY-MM-DD part
    }

    // If not in correct format, throw error
    throw new Error(t('SystemAdministrationAccountingPeriodInvalidDateFormat'));
  };

  // Initialize year from metadata or fallback to initialYear
  const [year, setYear] = useState<number>(initialYear);
  const [yearInitialized, setYearInitialized] = useState<boolean>(false);

  // Configuration from API
  const { data: configuration, isLoading: isLoadingConfiguration } =
    useGetAccountingPeriodConfiguration();
  const updateConfigurationMutation = useUpdateAccountingPeriodConfiguration();

  // Metadata from API
  const { data: metadata } = useGetAccountingPeriodMetadata();

  // Automatic mode execution
  const updateAccountingPeriodMode = useUpdateAccountingPeriodMode();

  // Update year when metadata is loaded (only once)
  useEffect(() => {
    if (metadata?.defaultYear && !yearInitialized) {
      setYear(metadata.defaultYear);
      setYearInitialized(true);
    }
  }, [metadata?.defaultYear, yearInitialized]);

  // Get selected month from configuration
  const getSelectedMonthFromConfiguration = (): number => {
    if (!configuration?.fiscalYearStartDate) return 1;

    try {
      const date = new Date(configuration.fiscalYearStartDate);
      return date.getMonth() + 1; // Convert to 1-12 range
    } catch {
      return 1;
    }
  };

  const selectedMonth = getSelectedMonthFromConfiguration();

  // API integration
  const { data: accountingPeriods, isLoading } = useGetAccountingPeriods(
    year,
    selectedMonth !== 1 ? selectedMonth : undefined,
  );

  // Create mutation
  const createMutation = useCreateAccountingPeriod();

  // Update mutation
  const updateMutation = useUpdateAccountingPeriod();

  // Update status mutation
  const updateStatusMutation = useUpdateAccountingPeriodStatus();

  // Row highlighting using MUI DataGrid's native capabilities
  const { highlightedRows = [], highlightRow } = useRowHighlighting(3000);
  const highlightedRowIds = highlightedRows.map((row) => row.id);

  // Dialog states
  const [editing, setEditing] = useState<AccountingPeriod | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AccountingPeriod | null>(
    null,
  );

  // Notification states
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Inline editing states
  const [isAddingNew, setIsAddingNew] = useState<boolean>(false);
  const [newPeriod, setNewPeriod] = useState<Partial<AccountingPeriod>>({
    status: AccountingPeriodStatus.Open,
    startDate: '',
    endDate: '',
  });
  const [newlyAddedPeriodId] = useState<string | null>(null);

  // Edit existing period states
  const [editingPeriodId, setEditingPeriodId] = useState<number | null>(null);
  const [editingPeriod, setEditingPeriod] = useState<AccountingPeriod | null>(
    null,
  );

  // Mode change confirmation states
  const [modeChangeConfirmation, setModeChangeConfirmation] = useState<{
    open: boolean;
    newMode: 'manual' | 'automatic' | null;
    onConfirm: (() => void | Promise<void>) | null;
    onCancel: (() => void | Promise<void>) | null;
  }>({
    open: false,
    newMode: null,
    onConfirm: null,
    onCancel: null,
  });

  // Get accounting period by ID when editing
  const { data: fetchedEditingPeriod, isLoading: isLoadingEditingPeriod } =
    useGetAccountingPeriodById(editingPeriodId ?? undefined);

  // Update editing period when fresh data is fetched (only once when editing starts)
  useEffect(() => {
    if (fetchedEditingPeriod && editingPeriodId) {
      // Set the initial editing period when fresh data is fetched
      setEditingPeriod(fetchedEditingPeriod);
    }
  }, [fetchedEditingPeriod, editingPeriodId]);

  // Helper function to get current mode
  const getCurrentMode = (): 'manual' | 'automatic' => {
    if (!configuration) return 'manual';
    return configuration.accountingPeriodAutomaticCreation === '1'
      ? 'automatic'
      : 'manual';
  };

  // Cancel form when mode changes to automatic
  useEffect(() => {
    const currentMode = getCurrentMode();
    if (currentMode === 'automatic') {
      // Cancel new period form
      if (isAddingNew) {
        setIsAddingNew(false);
        setNewPeriod({
          status: AccountingPeriodStatus.Open,
          startDate: '',
          endDate: '',
        });
      }
      // Cancel editing
      if (editingPeriod) {
        setEditingPeriodId(null);
        setEditingPeriod(null);
      }
    }
  }, [configuration?.accountingPeriodAutomaticCreation]);

  // Configuration change handler
  const handleConfigurationChange = async (
    newConfiguration: AccountingPeriodConfiguration,
  ) => {
    try {
      // Format fiscalYearStartDate for API (MM-DD format)
      const formattedConfiguration = {
        ...newConfiguration,
        fiscalYearStartDate: newConfiguration.fiscalYearStartDate
          ? (() => {
              const dateStr = newConfiguration.fiscalYearStartDate;
              if (dateStr.includes('/')) {
                const parts = dateStr.split('/');
                if (parts.length === 3) {
                  return `${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
                }
              } else if (dateStr.includes('-') && dateStr.length >= 10) {
                return dateStr.substring(5, 10);
              }
              return dateStr;
            })()
          : newConfiguration.fiscalYearStartDate,
      };

      await updateConfigurationMutation.mutateAsync(formattedConfiguration);
      // Only show success message for mode changes, not period type changes
      const isModeChange =
        newConfiguration.accountingPeriodAutomaticCreation !==
        configuration?.accountingPeriodAutomaticCreation;
      if (isModeChange) {
        showNotification(
          t('SystemAdministrationAccountingPeriodConfigurationUpdated'),
          'success',
        );
      }
    } catch (error) {
      console.error('Failed to execute automatic mode:', error);
      showNotification(
        t('SystemAdministrationAccountingPeriodFailedToUpdateConfiguration'),
        'error',
      );
    }
  };

  // Handle mode change confirmation
  const handleModeChangeConfirm = () => {
    const confirmCallback = modeChangeConfirmation.onConfirm;

    setModeChangeConfirmation({
      open: false,
      newMode: null,
      onConfirm: null,
      onCancel: null,
    });

    if (confirmCallback) {
      confirmCallback();
    }

    // Cancel any new period form when switching modes or executing automatic mode
    if (isAddingNew) {
      setIsAddingNew(false);
      setNewPeriod({
        status: AccountingPeriodStatus.Open,
        startDate: '',
        endDate: '',
      });
    }

    // Also cancel any editing in progress
    if (editingPeriod) {
      setEditingPeriodId(null);
      setEditingPeriod(null);
    }
  };

  // Handle mode change cancellation
  const handleModeChangeCancel = () => {
    if (modeChangeConfirmation.onCancel) {
      modeChangeConfirmation.onCancel();
    }
    setModeChangeConfirmation({
      open: false,
      newMode: null,
      onConfirm: null,
      onCancel: null,
    });
  };

  // Handle execute automatic mode
  const runAutomaticMode = async (
    configToUse: AccountingPeriodConfiguration,
  ) => {
    try {
      if (configToUse.accountingPeriodAutomaticCreation !== '1') {
        showNotification(
          t('SystemAdministrationAccountingPeriodSelectAutomaticModeFirst'),
          'error',
        );
        return;
      }

      if (!configToUse.accountingPeriodType) {
        const configWithDefaultType = {
          ...configToUse,
          accountingPeriodType: 'Month' as const,
        };
        await updateAccountingPeriodMode.mutateAsync(configWithDefaultType);
      } else {
        await updateAccountingPeriodMode.mutateAsync(configToUse);
      }

      showNotification(
        t(
          'SystemAdministrationAccountingPeriodAutomaticModeExecutedSuccessfully',
        ),
        'success',
      );
    } catch {
      showNotification(
        t('SystemAdministrationAccountingPeriodFailedToExecuteAutomaticMode'),
        'error',
      );
    }
  };

  const handleExecuteAutomaticMode = async (
    configToUse: AccountingPeriodConfiguration,
    options?: {
      onBeforeExecute?: () => void | Promise<void>;
      onCancel?: () => void | Promise<void>;
    },
  ) => {
    // Show confirmation dialog before executing
    setModeChangeConfirmation({
      open: true,
      newMode: null,
      onConfirm: async () => {
        if (options?.onBeforeExecute) {
          await options.onBeforeExecute();
        }
        await runAutomaticMode(configToUse);
      },
      onCancel: options?.onCancel ?? null,
    });
  };

  const currentMode = getCurrentMode();

  // Use API data directly
  const data = accountingPeriods || [];

  // Removed unused handler functions

  const handleEdit = (row: AccountingPeriod) => {
    setEditingPeriodId(row.accountingPeriodId);
    // Don't set editingPeriod here - let the useEffect handle it when fresh data is fetched
    setEditingPeriod(null);
  };

  const handleDelete = (row: AccountingPeriod) => {
    setConfirmDelete(row);
  };

  const handleAddPeriod = useCallback(() => {
    // Use React.startTransition for better responsiveness
    startTransition(() => {
      setIsAddingNew(true);

      // Get default dates from metadata if available
      let defaultStartDate = '';
      let defaultEndDate = '';

      if (
        metadata?.manualMode?.defaultStartDate &&
        metadata?.manualMode?.defaultEndDate
      ) {
        // Convert ISO dates to YYYY-MM-DD format for HTML date inputs
        try {
          // Extract date part directly from ISO string to avoid timezone conversion issues
          defaultStartDate = metadata.manualMode.defaultStartDate.split('T')[0];
          defaultEndDate = metadata.manualMode.defaultEndDate.split('T')[0];
        } catch (error) {
          console.warn('Failed to parse default dates from metadata:', error);
        }
      }

      setNewPeriod({
        status: AccountingPeriodStatus.Open,
        startDate: defaultStartDate,
        endDate: defaultEndDate,
      });
    });
  }, [metadata]);

  const handleSaveNewPeriod = async () => {
    // Enhanced validation
    if (!newPeriod.startDate?.trim()) {
      showNotification(
        t('SystemAdministrationAccountingPeriodStartDateRequired'),
        'error',
      );
      return;
    }

    if (!newPeriod.endDate?.trim()) {
      showNotification(
        t('SystemAdministrationAccountingPeriodEndDateRequired'),
        'error',
      );
      return;
    }

    try {
      // Validate dates before sending
      const startDate = ensureYYYYMMDD(newPeriod.startDate);
      const endDate = ensureYYYYMMDD(newPeriod.endDate);

      if (startDate >= endDate) {
        showNotification(
          t('SystemAdministrationAccountingPeriodEndDateMustBeAfterStartDate'),
          'error',
        );
        return;
      }

      const createData: CreateAccountingPeriodRequest = {
        startDate,
        endDate,
      };

      const newPeriodResponse = await createMutation.mutateAsync(createData);

      // Highlight the newly created row with a small delay to ensure DOM is updated
      setTimeout(() => {
        highlightRow(newPeriodResponse.accountingPeriodId);
      }, 100);

      // Show success notification
      showNotification(
        t('SystemAdministrationAccountingPeriodCreatedSuccessfully'),
        'success',
      );

      // Reset the form
      setIsAddingNew(false);
      setNewPeriod({
        status: AccountingPeriodStatus.Open,
        startDate: '',
        endDate: '',
      });
    } catch (error) {
      // Extract the actual error message from the API response
      let errorMessage = t(
        'SystemAdministrationAccountingPeriodUnexpectedError',
      );

      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as {
          response?: { data?: { errors?: Record<string, string[]> } };
        };
        const responseData = apiError.response?.data;
        if (responseData?.errors) {
          // Handle validation errors structure: { errors: { "": ["error message"] } }
          const errorEntries = Object.values(responseData.errors);

          if (
            errorEntries.length > 0 &&
            Array.isArray(errorEntries[0]) &&
            errorEntries[0].length > 0
          ) {
            errorMessage = errorEntries[0][0]; // Get the first error message
          }
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        } else if (responseData?.title) {
          errorMessage = responseData.title;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      showNotification(errorMessage, 'error');
    }
  };

  const handleCancelNewPeriod = () => {
    setIsAddingNew(false);
    setNewPeriod({
      status: AccountingPeriodStatus.Open,
      startDate: '',
      endDate: '',
    });
  };

  const handleSaveEditingPeriod = async () => {
    if (!editingPeriod) return;

    // Enhanced validation
    if (!editingPeriod.startDate?.trim()) {
      showNotification(
        t('SystemAdministrationAccountingPeriodStartDateRequired'),
        'error',
      );
      return;
    }

    if (!editingPeriod.endDate?.trim()) {
      showNotification(
        t('SystemAdministrationAccountingPeriodEndDateRequired'),
        'error',
      );
      return;
    }

    try {
      // Validate dates before sending
      const startDate = ensureYYYYMMDD(editingPeriod.startDate);
      const endDate = ensureYYYYMMDD(editingPeriod.endDate);

      if (startDate >= endDate) {
        showNotification(
          t('SystemAdministrationAccountingPeriodEndDateMustBeAfterStartDate'),
          'error',
        );
        return;
      }

      // Check if status has changed - if so, use status update API
      const originalPeriod = data?.find(
        (p) => p.accountingPeriodId === editingPeriod.accountingPeriodId,
      );
      const statusChanged =
        originalPeriod && originalPeriod.status !== editingPeriod.status;

      let updatedPeriodResponse;

      if (statusChanged) {
        // Use status update API
        const statusUpdateData: UpdateAccountingPeriodStatusRequest = {
          accountingPeriodId: editingPeriod.accountingPeriodId,
          startDate,
          endDate,
          status: editingPeriod.status,
        };

        updatedPeriodResponse = await updateStatusMutation.mutateAsync({
          id: editingPeriod.accountingPeriodId,
          data: statusUpdateData,
        });
      } else {
        // Use regular update API (dates only)
        const updateData: UpdateAccountingPeriodRequest = {
          startDate,
          endDate,
        };
        updatedPeriodResponse = await updateMutation.mutateAsync({
          id: editingPeriod.accountingPeriodId,
          data: updateData,
        });
      }

      // Highlight the updated row with a small delay to ensure DOM is updated
      setTimeout(() => {
        highlightRow(updatedPeriodResponse.accountingPeriodId);
      }, 100);

      // Show success notification
      showNotification(
        t('SystemAdministrationAccountingPeriodUpdatedSuccessfully'),
        'success',
      );

      // Reset the editing state
      setEditingPeriodId(null);
      setEditingPeriod(null);
    } catch (error) {
      // Extract the actual error message from the API response
      let errorMessage = t(
        'SystemAdministrationAccountingPeriodUnexpectedError',
      );

      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as {
          response?: { data?: { errors?: Record<string, string[]> } };
        };
        const responseData = apiError.response?.data;
        if (responseData?.errors) {
          // Handle validation errors structure: { errors: { "": ["error message"] } }
          const errorEntries = Object.values(responseData.errors);
          if (
            errorEntries.length > 0 &&
            Array.isArray(errorEntries[0]) &&
            errorEntries[0].length > 0
          ) {
            errorMessage = errorEntries[0][0]; // Get the first error message
          }
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        } else if (responseData?.title) {
          errorMessage = responseData.title;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      showNotification(errorMessage, 'error');
    }
  };

  const handleCancelEditingPeriod = () => {
    setEditingPeriodId(null);
    setEditingPeriod(null);
  };

  const handleEditingPeriodChange = (
    updatedPeriod: Partial<AccountingPeriod>,
  ) => {
    if (editingPeriod) {
      const newEditingPeriod = { ...editingPeriod, ...updatedPeriod };
      setEditingPeriod(newEditingPeriod);
    }
  };

  const showNotification = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
  ) => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleDeleteSuccess = () => {
    showNotification(
      t('SystemAdministrationAccountingPeriodDeletedSuccessfully'),
      'success',
    );
  };

  const handleDeleteError = (error: Error) => {
    showNotification(
      t('SystemAdministrationAccountingPeriodFailedToDelete', {
        error: error.message,
      }),
      'error',
    );
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleDoubleClickEvent: GridEventListener<'cellDoubleClick'> = (
    params,
  ) => {
    if (params.field === 'accountingPeriodId') {
      handleEdit(params.row);
    }
  };

  // Removed old column definitions - now handled in AccountingPeriodDataGrid component
  const BLOCK_PAD_Y = 1; // padding inside each section (theme.spacing(2) = 16px)
  const LINE_GAP_Y = 0; // space above/below the divider line

  return (
    <SettingsContentWrapper
      header={title || t('SystemAdministrationAccountingPeriodYearsAndPeriods')}
      description={
        description || t('SystemAdministrationAccountingPeriodDescription')
      }
    >
      <Stack
        divider={<Divider sx={{ my: LINE_GAP_Y, borderColor: 'divider' }} />}
        sx={{ '& > *': { py: BLOCK_PAD_Y } }} // uniform vertical padding for all sections
      >
        {/* Fiscal Year and Mode Selection */}

        <Stack gap={2}>
          <AccountingPeriodModeSelector
            configuration={configuration}
            onConfigurationChange={handleConfigurationChange}
            onExecuteAutomaticMode={handleExecuteAutomaticMode}
            availablePeriodTypes={metadata?.availablePeriodTypes}
            isLoading={
              isLoadingConfiguration ||
              updateConfigurationMutation.isPending ||
              updateAccountingPeriodMode.isPending
            }
          />
        </Stack>

        {/* Controls Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          {/* Left side: YearFilter */}
          <YearFilter
            value={year}
            onChange={setYear}
            variant="chips"
            maxVisibleYears={5}
            years={metadata?.availableYears}
            fiscalYearStartMonth={selectedMonth}
            data-automation-id="AccountingPeriodYearFilter"
          />

          {/* Right side: New Period Button - Hidden when Automatic mode is selected */}
          {currentMode === 'manual' && (
            <Button variant="contained" size="small" onClick={handleAddPeriod}>
              {t('SystemAdministrationAccountingPeriodNewPeriod')}
            </Button>
          )}
        </Stack>

        {/* DataGrid Section */}
        <Stack>
          <AccountingPeriodDataGrid
            data={data}
            loading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCellDoubleClick={handleDoubleClickEvent}
            isAddingNew={isAddingNew}
            newPeriod={newPeriod}
            onNewPeriodChange={setNewPeriod}
            onSaveNewPeriod={handleSaveNewPeriod}
            onCancelNewPeriod={handleCancelNewPeriod}
            newlyAddedPeriodId={newlyAddedPeriodId}
            mode={currentMode}
            highlightedRowIds={highlightedRowIds}
            editingPeriod={editingPeriod}
            onEditingPeriodChange={handleEditingPeriodChange}
            onSaveEditingPeriod={handleSaveEditingPeriod}
            onCancelEditingPeriod={handleCancelEditingPeriod}
            isLoadingEditingPeriod={isLoadingEditingPeriod}
          />
        </Stack>
      </Stack>

      {/* Modal Dialogs - Outside main content flow to prevent dividers */}
      <AccountingPeriodDialog
        period={editing}
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
      />

      <DeleteConfirmDialog
        period={confirmDelete}
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onDeleteSuccess={handleDeleteSuccess}
        onDeleteError={handleDeleteError}
        title={t('SystemAdministrationAccountingPeriodDeleteConfirmationTitle')}
        message={t(
          'SystemAdministrationAccountingPeriodDeleteConfirmationMessage',
        )}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <ConfirmationDialog
        open={modeChangeConfirmation.open}
        onClose={handleModeChangeCancel}
        onConfirm={handleModeChangeConfirm}
        title={
          modeChangeConfirmation.newMode
            ? t('SystemAdministrationAccountingPeriodChangeModeConfirmation')
            : t('SystemAdministrationAccountingPeriodExecuteAutomaticMode')
        }
        message={t('SystemAdministrationAccountingPeriodChangeAffect')}
        confirmLabel={t('SystemAdministrationAccountingPeriodExecute')}
        cancelLabel={t('SystemAdministrationAccountingPeriodCancel')}
        variant="warning"
        customErrorMessage={t(
          'SystemAdministrationAccountingPeriodUnexpectedError',
        )}
        data-automation-id="AccountingPeriodModeChangeConfirmationDialog"
      />
    </SettingsContentWrapper>
  );
};

export { AccountingPeriodPage };
